import React, { useState, useEffect, useRef, useCallback } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import {
  getFormatter,
  postFormatFields,
  preFormatFields,
} from "../field-formatter"
import LocalizationController from "aldoo-ra/Localization/localization-controller"
import GroupController from "./groups"
import validateFields from "./validate-fields"
import CopyPaste from "./copy-paste"
import { applyAllFieldModifiers } from "../field-modifier"
import { useHasPermission } from "aldoo-ra/Permissions"
import _ from "lodash"
import { ArrowUpTrayIcon, CloudArrowUpIcon, ClockIcon, ChevronLeftIcon } from "@heroicons/react/24/outline"
import UpdatesController from "aldoo-ra/Admin/Updates/controller/updates-controller"
import CreateUpdateDialog from "aldoo-ra/Admin/Updates/controller/create-update-dialog"
import EnvironmentContentInstallDialog from "aldoo-ra/Admin/Updates/Environments/EnvironmentContentInstallDialog"
import ContentHistoryDialog from "./ContentHistoryDialog"
import ContentSettingsDialog from "./ContentSettingsDialog"
import FieldQuickSearch from "./field-quick-search"
import Tooltip from "aldoo-ra/Tooltip"
import { url } from "aldoo-ra/CMS/url"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import PubSub from "pubsub-js"
import { getTypeEditor } from "../type-editor-resolver"
import { FieldClipboardProvider } from "../field-clipboard-context"
import { useVisibilityContext } from "./section-visibility"
import SaveSummaryDialog, { formChanges, revertedValue, showChangesOnSave, setShowChangesOnSave } from "./save-summary"

const truncate = (str, n = 80) => {
  return str?.length > n ? str.substr(0, n - 1) + "..." : str
}

export default function ContentEditor({
  contentType,
  fieldValues = {},
  validators,
  formatters,
  onClickSave,
  onSave,
  saveLabel = "Save",
  // the host's words for the editor's own (the save summary, the Save button): t`English`, t("…%s…", n)
  t,
  title,
  parentValues = null,
  parentObject = null,
  onError,
  dataItem,
  //Usually when editing types, the fields don't expose the settings
  enableFieldSettings = true,
  variant = "default",
  realtimeValidators,
  // Control whether to clear field values after save (default: true for backward compatibility)
  // Emptying the form after a save is for editors that go on to a new
  // record; one that stays open on the same record (the product dialog) was
  // left with nothing, and its next save lost every field. Opt in.
  clearValuesAfterSave = false,
  // Control visibility of toolbar buttons
  showCopyPaste = true,
  showExport = true,
  showSearch = true,
  // Auto-save when field values change
  // the url parameter the active group tab is kept in (opt-in)
  tabParam,
  // "vertical" renders the group tabs as a rail on the left
  tabsOrientation,
  // the help center's key of a tab, shown as a "?" beside its name in the rail
  // (aldoo-ra/HelpCenter/InPlaceHelp): helpKeyOf(tabId) → "vendor.product#<tabId>"
  helpKeyOf = null,
  autoSave = false,
  autoSaveDelay = 1000, // Delay in ms before triggering auto-save
  // Callback to refetch content data from server
  onRefresh,
  // Hide panel wrapper for groups
  hidePanel = false,
  hideSaveButton = false,
  hideHeader = false,
  onFieldChange,
  // How groups are rendered: "tabs" (default) or "foldable"
  renderGroupsAs = "tabs",
  // The language the sections edit in, when the host tracks one
  language,
  // Components rendered in the header next to Save, for what is not a field:
  // called with { dataItem, contentType, values, onChange, language, close }.
  // The content type may carry its own in `actions`, and they render inside
  // the section providers, so a shared section editor is theirs too.
  actions,
  // Close the editor — the host's, handed to the header actions
  onClose,
  // A back chevron before the title, when the host wants one
  onBack,
  // Save asks first, listing what changed: the form's fields against what was
  // loaded, and what the sections report (`changes()` beside their `save`).
  // Unset: the reader's setting for the content type (Content Settings → Show
  // Changes On Save, on by default); true / false decides it for this editor
  confirmChanges,
}) {
  
  // Guard clause: Return early if contentType is not properly defined
  if (!contentType) {
    return (
      <div className="p-4 text-center">
        <p className="text-admin_text">Content type is required to render the editor.</p>
      </div>
    );
  }
  
  const [localFieldValues, setLocalFieldValues] = useState({}) // Local copy of field values
  const [errors, setErrors] = useState({}) // Validation errors
  const [validationStatus, setValidationStatus] = useState({}) // Real-time validation status
  // The content tools — copy/paste, settings, history, export, install — act on
  // the whole record, across environments: a system admin's (role:admin), not
  // a vendor's or a reader's, whoever else may open this editor.
  const { hasPermission: isSystemAdmin } = useHasPermission("role:admin")
  const [showExportDialog, setShowExportDialog] = useState(false) // Export dialog state
  const [showInstallDialog, setShowInstallDialog] = useState(false) // Install dialog state
  const [showHistoryDialog, setShowHistoryDialog] = useState(false) // History dialog state
  const [showSettingsDialog, setShowSettingsDialog] = useState(false) // Settings dialog state
  const [fieldSearchData, setFieldSearchData] = useState({ searchTerm: "", filteredFields: [], hasActiveSearch: false }) // Quick search data
  const localizationController = LocalizationController()
  const exportController = UpdatesController()
  const initializedRef = useRef(false) // Track if we've already initialized with these field values
  const fieldValuesRef = useRef(null) // Track the current field values
  const autoSaveTimeoutRef = useRef(null) // Track auto-save timeout
  const localizationToggleInProgressRef = useRef(false) // Track if localization is being toggled
  const sectionsRef = useRef({}) // { dirty, save } of the groups that render their own component
  // the values as loaded, so a save can say which fields it changed: a form
  // written back whole reverts whatever else wrote the record meanwhile (a
  // title synced to the store text, an uploaded deliverable, a counter)
  const baselineRef = useRef(null)

  // A section (a group with a `component`) persists through its own editor, so
  // it hands the content editor a save that runs with this one's. A ref keeps
  // it out of the render cycle — registering must never re-render the editor.
  // Whether a section holds unsaved changes ({ dirty: true }) is shown under Save: the
  // editor re-renders only when that answer turns, never on a registration alone.
  const [sectionsDirty, setSectionsDirty] = useState(false)
  const sectionsDirtyRef = useRef(false)
  const registerSection = useCallback((id, handlers) => {
    if (!id) return
    if (!handlers) delete sectionsRef.current[id]
    else sectionsRef.current[id] = handlers
    const dirty = Object.values(sectionsRef.current).some((entry) => entry?.dirty === true)
    if (dirty === sectionsDirtyRef.current) return
    sectionsDirtyRef.current = dirty
    setSectionsDirty(dirty)
  }, [])
  // a save makes what it wrote the new "as loaded"
  const [, setBaselineTick] = useState(0)
  const [summary, setSummary] = useState(null) // { changes } while the save summary is open
  const [summarySaving, setSummarySaving] = useState(false)
  // the reader's own setting for this content type, unless the host decides
  const [askOnSave, setAskOnSave] = useState(() => showChangesOnSave(contentType?.type))
  useEffect(() => setAskOnSave(showChangesOnSave(contentType?.type)), [contentType?.type])
  const confirms = confirmChanges ?? askOnSave
  // a change taken back re-reads the list once the sections have rendered what they hold now
  const [summaryRefresh, setSummaryRefresh] = useState(0)

  //parent values getter
  if (!parentValues) parentValues = () => localFieldValues

  // Auto-save function
  const triggerAutoSave = useCallback((updatedValues = null) => {
    if (!autoSave || !onSave) return
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }
    
    // Use the passed values or fall back to current localFieldValues
    const valuesToSave = updatedValues || localFieldValues
    
    // Set new timeout
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        // Call onSave directly with the updated values
        await onSave(valuesToSave)
      } catch (error) {
        console.error('Auto-save failed:', error)
      }
    }, autoSaveDelay)
  }, [autoSave, onSave, autoSaveDelay, localFieldValues])

  const save = async () => {
    if (
      !(await validateFields({
        contentType,
        localFieldValues,
        validators,
        parentValues,
        setErrors,
      }))
    ) {
      announceError()
      return
    }

    // The sections go first: their own validation may stop the save. A
    // section may answer with the fields it wrote ({ stock: 10 }): the form
    // takes them, or its own save would write back what it loaded
    const sectionValues = {}
    for (const handlers of Object.values(sectionsRef.current)) {
      if (!handlers?.save || handlers.dirty === false) continue
      const saved = await handlers.save()
      if (saved === false) return
      if (saved && typeof saved === "object") Object.assign(sectionValues, saved)
    }
    if (Object.keys(sectionValues).length) setLocalFieldValues((prev) => ({ ...prev, ...sectionValues }))

    //there is no save method
    if (!onSave) return

    // the fields this save changes, against what was loaded (null when there
    // is nothing to compare with: a new record is written whole)
    const current = { ...localFieldValues, ...sectionValues }
    const baseline = baselineRef.current
    const changed = baseline ? Object.keys(current).filter((key) => !_.isEqual(current[key], baseline[key])) : null

    // Post format the field values before saving
    const postFormattedValues = await postFormatFields(
      { ...current },
      contentType
    )

    // Run Type.formatter if specified (this is usually used to format DatabaseModel types)
    if (contentType.formatter) {
      const formatter = getFormatter(contentType.formatter)
      if (formatter?.postFormat) {
        await formatter.postFormat(postFormattedValues, contentType)
      }
    }

    // Run any custom post formatters
    formatters &&
      (await Promise.all(
        formatters.map(async (formatter) => {
          if (formatter.postFormat) {
            await formatter.postFormat(postFormattedValues, contentType)
          }
        })
      ))
    //return the save response to the caller, with what changed
    const response = await onSave(postFormattedValues, { changed })
    // what was written is what is loaded now: nothing is pending until the next edit
    if (response) {
      baselineRef.current = current
      setBaselineTick((tick) => tick + 1)
    }
    return response
  }

  // Create validation status provider function
  const validationStatusProvider = (fieldId) => {
    return validationStatus[fieldId] || null
  }

  // Create real-time validator trigger function
  const triggerRealtimeValidation = async (fieldId, value, allFieldValues) => {
    if (!realtimeValidators) return

    const fieldValidators = realtimeValidators(setValidationStatus)
    const fieldValidator = fieldValidators[fieldId]
    
    if (fieldValidator) {
      try {
        await fieldValidator(value, allFieldValues)
      } catch (error) {
        console.error(`Real-time validation error for field ${fieldId}:`, error)
      }
    }
  }

  // What the groups' visibility rules are asked with
  const visibilityContext = useVisibilityContext(contentType, {
    product: dataItem,
    dataItem,
    contentType,
    language,
  })

  const { render: renderGroups } = GroupController({
    contentType,
    localFieldValues,
    setLocalFieldValues,
    parentValues,
    parentObject,
    errors,
    setErrors,
    enableFieldSettings,
    dataItem,
    save,
    variant,
    triggerAutoSave,
    validationStatusProvider,
    triggerRealtimeValidation,
    fieldSearchData,
    localizationToggleInProgressRef,
    hidePanel,
    renderGroupsAs,
    language,
    registerSection,
    visibilityContext,
    tabParam,
    tabsOrientation,
    helpKeyOf,
  })

  //handle on error
  const announceError = () => {
    if (!onError) return
    if (Object.keys(errors).length === 0) return
    //check if any error exists
    const hasError = Object.values(errors).some((error) => error !== null)
    if (!hasError) return
    //announce the error
    onError(errors)
  }


  const syncLocalizedFields = async (fieldValues, contentType) => {
    //find all fields that are localized and update their values
    const keys = dataItem.localization?.map((item) => item.localizationKey)

    if (!keys || keys.length == 0) return

    const result = await localizationController.getLocalizationKeys("en", keys)

    //update the field values
    for (const key in result) {
      const content = result[key]
      const fieldKey = key.split("-")[1]
      //skip the content if it's the same as the key
      //this means the field is not localized
      if (content === `{{${key}}}`) continue
      const field = contentType.fields?.find((field) => field.id === fieldKey)
      if (!field) continue
      const fieldType = field.type.toLowerCase()

      fieldValues[fieldKey] = localizationController.applyPreFormatters(
        content,
        fieldType
      )
    }
  }

  useEffect(() => {
    if (!fieldValues) return

    // Skip entirely if localization toggle is in progress
    if (localizationToggleInProgressRef.current) {
      localizationToggleInProgressRef.current = false
      return
    }

    // Check if these are the same fieldValues we already processed
    const fieldValuesString = JSON.stringify(fieldValues);
    if (fieldValuesRef.current === fieldValuesString) {
      return
    }


    let processingCancelled = false;

    ~(async () => {
      //sync the localized fields if the localization is enabled
      if (dataItem?.localization?.length > 0) {
        await syncLocalizedFields(fieldValues, contentType)
      }

      const preFormattedValues = contentType?.fields
        ? await preFormatFields({ ...fieldValues }, contentType)
        : { ...fieldValues }

      // Seed field defaults into the form state so a field's default value
      // exists even when its editor isn't mounted (e.g. inside a collapsed
      // group). Without this, defaults relied on each editor's onChange-on-
      // mount, so a defaulted-but-unmounted field (e.g. a checked-by-default
      // boolean like `enabled`) stayed undefined and failed "required".
      // Only fills genuinely missing values — never overrides existing ones.
      if (contentType?.fields) {
        for (const f of contentType.fields) {
          if (f.default !== undefined && preFormattedValues[f.id] === undefined) {
            preFormattedValues[f.id] = f.default
          }
        }
      }


      //run Type.formatter if specified ( this is usually used to format DatabaseModel types )
      if (contentType && contentType.formatter) {
        const formatter = getFormatter(contentType.formatter)
        if (formatter && formatter.preFormat) {
          await formatter.preFormat(preFormattedValues, contentType)
        }
      }

      //run any custom pre formatters
      // Using Promise.all with map
      formatters &&
        (await Promise.all(
          formatters?.map(async (formatter) => {
            if (!formatter.preFormat) return
            return formatter.preFormat(preFormattedValues, contentType)
          })
        ))

      // Apply field modifiers on initial load to ensure computed fields are set
      const valuesWithModifiers = applyAllFieldModifiers(
        contentType?.type,
        preFormattedValues
      )

      // Only set field values if processing wasn't cancelled
      if (!processingCancelled) {
        // Update refs after successful processing
        fieldValuesRef.current = fieldValuesString;

        setLocalFieldValues(valuesWithModifiers) // Set the initial field values on mount
        baselineRef.current = valuesWithModifiers
      }
    })()

    return () => {
      processingCancelled = true;
      // Only clear values when the component is truly unmounting, not on every fieldValues change
      // setLocalFieldValues({}) // Commented out - this was causing field values to clear on group toggle
    }
  }, [fieldValues])

  // Subscribe to content download completed event
  useEffect(() => {
    const token = PubSub.subscribe("content-download-completed", async (msg, data) => {
      // Check if the download is for this content
      if (dataItem?._id && data.contentId === dataItem._id) {
        // Call onRefresh to refetch the content from the server
        if (onRefresh) {
          onRefresh()
        } else {
          // If no onRefresh callback provided, fetch and update locally with retry logic
          const refreshSelf = async (retryCount = 0) => {
            if (retryCount > 0) {
              const delay = 300 * Math.pow(2, retryCount - 1);
              await new Promise(resolve => setTimeout(resolve, delay));
            }

            try {
              // Check if this is a database model or CMS content
              const dbResolver = DatabaseModelTypeResolver();
              const isDBModel = dbResolver.getType(data.contentType) !== null;

              let result;
              if (isDBModel) {
                // Fetch directly from the database model using CRUD API
                const { api } = await import("aldoo-ra/API");
                const crudAPI = api("crud");
                result = await crudAPI({
                  get: {
                    model: data.contentType,
                    match: { _id: dataItem._id }
                  }
                });
              } else {
                // Fetch from CMSContent
                const { GetCMSContent } = await import("../api");
                result = await GetCMSContent({ match: { _id: dataItem._id } });
              }

              // Handle different response formats
              // CRUD API returns array directly, CMS API returns {data: [...]}
              const resultData = Array.isArray(result) ? result : result?.data;

              if (resultData && resultData.length > 0) {
                const freshData = resultData[0];

                // Check if content has been updated by comparing updatedAt
                if (freshData.updatedAt === dataItem.updatedAt && retryCount < 3) {
                  return refreshSelf(retryCount + 1);
                }

                // Extract content fields based on whether it's CMSContent or direct DB model
                const freshFieldValues = freshData.content
                  ? { ...freshData.content, _id: freshData._id }
                  : { ...freshData };

                // Force update by clearing ref and setting new values
                fieldValuesRef.current = null;

                // Trigger a re-process of field values
                const preFormattedValues = contentType?.fields
                  ? await preFormatFields({ ...freshFieldValues }, contentType)
                  : { ...freshFieldValues };

                if (contentType && contentType.formatter) {
                  const formatter = getFormatter(contentType.formatter);
                  if (formatter && formatter.preFormat) {
                    await formatter.preFormat(preFormattedValues, contentType);
                  }
                }

                const valuesWithModifiers = applyAllFieldModifiers(
                  contentType?.type,
                  preFormattedValues
                );

                setLocalFieldValues(valuesWithModifiers);
                baselineRef.current = valuesWithModifiers;

                // Publish event to notify that refresh is complete
                PubSub.publish("content-refresh-completed", {
                  contentId: dataItem._id,
                  contentType: data.contentType
                });
              }
            } catch (error) {
              console.error("Failed to refresh content:", error);
              // Publish event even on error so UI can reset
              PubSub.publish("content-refresh-completed", {
                contentId: dataItem._id,
                contentType: data.contentType,
                error: true
              });
            }
          };

          await refreshSelf();
        }
      }
    })

    return () => {
      PubSub.unsubscribe(token)
    }
  }, [dataItem?._id, onRefresh, dataItem?.updatedAt, contentType])

  const handleSaveClick = async () => {
    if (!onClickSave) return
    if (
      !(await validateFields({
        contentType,
        localFieldValues,
        validators,
        setErrors,
        parentValues,
      }))
    ) {
      announceError()
      return
    }

    // ask first, with what is about to be written (a new record is all new: nothing to compare);
    // with nothing changed there is nothing to ask: it is saved as it is, without the dialog
    if (confirms && dataItem?._id) {
      const changes = collectChanges()
      if (changes.length) {
        setSummary({ changes })
        return
      }
    }
    await runSave()
  }

  // what a save would write: the form's fields, then the sections' own (each with its `revert`)
  const collectChanges = () => {
    const sectionChanges = Object.values(sectionsRef.current).flatMap((handlers) => {
      try {
        return handlers?.changes ? handlers.changes() || [] : []
      } catch (_) {
        return []
      }
    })
    return [...formChanges(localFieldValues, baselineRef.current, contentType), ...sectionChanges]
  }

  const revertChange = (change) => {
    if (typeof change.revert === "function") change.revert()
    else if (change.key) setLocalFieldValues((prev) => ({ ...prev, [change.key]: revertedValue(prev, baselineRef.current, change) }))
    setSummaryRefresh((tick) => tick + 1)
  }

  // every change, or the ones the summary's search shows
  const revertAll = (list = summary?.changes || []) => {
    const all = list.length === (summary?.changes || []).length
    for (const change of list) if (typeof change.revert === "function") change.revert()
    const baseline = baselineRef.current || {}
    if (all) {
      // the whole fields as loaded
      const keys = [...new Set(list.filter((change) => change.key).map((change) => change.key))]
      setLocalFieldValues((prev) => ({ ...prev, ...Object.fromEntries(keys.map((key) => [key, baseline[key]])) }))
    } else {
      // one change after the other: an entry of a list goes back alone
      setLocalFieldValues((prev) => list.filter((change) => change.key && typeof change.revert !== "function").reduce((values, change) => ({ ...values, [change.key]: revertedValue(values, baseline, change) }), prev))
    }
    setSummaryRefresh((tick) => tick + 1)
  }

  // after a change was taken back: the list as it stands, closed when nothing is left
  useEffect(() => {
    if (!summaryRefresh || !summary) return
    const changes = collectChanges()
    setSummary(changes.length ? { changes } : null)
  }, [summaryRefresh])

  const runSave = async () => {
    const result = await save()
    onClickSave(result)
    if (!result) return
    // Only clear values after save if clearValuesAfterSave is true
    if (clearValuesAfterSave) {
      setLocalFieldValues({})
    }
  }

  const handleExport = () => {
    if (!dataItem?._id) {
      console.warn("Cannot export: No content ID found")
      return
    }

    // Clear any existing configurations
    exportController.clearItems()

    // Create export configuration for this specific content item
    const exportConfig = exportController.create()

    // Determine if this is a content type or content based on the contentType
    const isContentType = contentType?.type === "ContentType"

    if (isContentType) {
      exportConfig.type = { label: "Content Type", dataType: "model", value: "CMSType" }
      exportConfig.details = `Type: ${title || localFieldValues.type || "Untitled"}`
      // Add match query to export only this specific CMSType
      exportConfig.query = { match: { _id: dataItem._id } }
    } else {
      // Check if contentType.type is a database model (using DatabaseModelTypeResolver)
      const dbResolver = DatabaseModelTypeResolver()
      const modelType = contentType?.type || "CMSContent"
      const isDBModel = dbResolver.getType(modelType) !== null

      // If it's a database model, export as the specific model type
      // Otherwise, check if it's content with content.type (like MenuItem)
      if (isDBModel) {
        exportConfig.type = {
          label: modelType,
          dataType: "model",
          value: modelType
        }
        exportConfig.details = `${modelType}: ${title || localFieldValues.name || localFieldValues.title || "Untitled"}`
      } else if (dataItem?.content?.type) {
        // This is content with a content.type field (like MenuItem)
        // Check if the content.type is a database model
        const contentTypeValue = dataItem.content.type
        const isContentTypeDBModel = dbResolver.getType(contentTypeValue) !== null

        if (isContentTypeDBModel) {
          // Export as the database model type
          exportConfig.type = {
            label: contentTypeValue,
            dataType: "model",
            value: contentTypeValue
          }
          exportConfig.details = `${contentTypeValue}: ${title || dataItem.content.name || dataItem.content.label || "Untitled"}`
          exportConfig.query = { match: { "content.type": contentTypeValue, _id: dataItem._id } }
        } else {
          // Export as generic content with type filter
          exportConfig.type = {
            label: "Content",
            dataType: "model",
            value: "CMSContent"
          }
          exportConfig.details = `Content (${contentTypeValue}): ${title || dataItem.content.name || dataItem.content.label || "Untitled"}`
          exportConfig.query = { match: { "content.type": contentTypeValue, _id: dataItem._id } }
        }
      } else {
        // Generic content without specific type
        exportConfig.type = {
          label: "Content",
          dataType: "model",
          value: "CMSContent"
        }
        exportConfig.details = `Content: ${title || localFieldValues.name || localFieldValues.title || "Untitled"}`
      }
    }

    // Set default query if not already set
    if (!exportConfig.query) {
      exportConfig.query = { match: { _id: dataItem._id } }
    }
    exportConfig.itemCount = 1

    // Add the configuration to the controller
    exportController.add(exportConfig)

    // Show the export dialog
    setShowExportDialog(true)
  }

  // The header's own actions: the host's, then the type's. They render inside
  // the section providers, so an action may drive the same editor a section does.
  // An action marked `last` (Action.last = true) goes after the others, beside the
  // fixed tools: one that is always there keeps its place while others come and go.
  const allActions = [
    ...(Array.isArray(actions) ? actions : actions ? [actions] : []),
    ...(contentType.actions || []),
  ]
  const headerActions = [...allActions.filter((Action) => !Action?.last), ...allActions.filter((Action) => Action?.last)]

  // What the type shows under the title: the extensions' own, rendered where
  // the label is and inside the same providers, so a badge may read the editor
  // a section drives.
  const subtitles = contentType.subtitles || []

  const renderSubtitles = () =>
    subtitles.map((Subtitle, index) => (
      <Subtitle key={index} dataItem={dataItem} contentType={contentType} values={localFieldValues} language={language} />
    ))

  const renderHeaderActions = () =>
    headerActions.map((Action, index) => (
      <Action
        key={index}
        dataItem={dataItem}
        contentType={contentType}
        values={localFieldValues}
        onChange={(values) => setLocalFieldValues((prev) => ({ ...prev, ...values }))}
        language={language}
        close={onClose}
      />
    ))

  // The type's section providers wrap the whole editor — the header included,
  // so its actions share the state the sections do, and above the tab switch,
  // so that state survives a tab change.
  const withSectionProviders = (content) => {
    const providers = contentType?.sectionProviders
    if (!providers?.length) return content
    return providers.reduceRight(
      (children, Provider) => (
        <Provider dataItem={dataItem} contentType={contentType} language={language} register={registerSection}>
          {children}
        </Provider>
      ),
      content
    )
  }

  return (
    <FieldClipboardProvider>
      <SaveSummaryDialog
        t={t}
        open={!!summary}
        changes={summary?.changes || []}
        saving={summarySaving}
        onCancel={() => setSummary(null)}
        onRevert={revertChange}
        onRevertAll={revertAll}
        onConfirm={async () => {
          setSummarySaving(true)
          try {
            await runSave()
          } finally {
            setSummarySaving(false)
            setSummary(null)
          }
        }}
      />
    {withSectionProviders(
    <div className="flex flex-col h-full">
      {/* Top Bar - Sticky Header */}
      {!hideHeader && (
        <div className="sticky top-0 z-10 bg-admin_paper rounded-t-lg shadow-sm p-2 md:p-4 border-b border-admin_bordered w-full flex flex-col justify-between items-center gap-3">
          {/* 1st Row : title & subtitle with buttons. On a phone the row is
              three: back on the left with Save on the right, then the thumb
              and the title, then the record's own actions — the two groups
              dissolve (`contents`) so their children order themselves */}
          <div className="w-full flex flex-wrap items-center gap-3 justify-between">
            <div className="contents md:flex md:items-center md:gap-3 md:min-w-0">
              {onBack && (
                <div className="order-1 md:order-none">
                  <Tooltip content="Back" position="top">
                    <Button variant="admin_icon" className="text-admin_text" onClick={onBack}>
                      <ChevronLeftIcon className="w-5 h-5" />
                    </Button>
                  </Tooltip>
                </div>
              )}

              <div className="order-3 md:order-none w-full md:w-auto md:flex-1 flex items-center gap-3 min-w-0">
              {/* Thumbnail if available */}
              {(dataItem?.thumbnail?.url || dataItem?.thumbnail) && (
                <img
                  src={url(dataItem.thumbnail?.url || dataItem.thumbnail)}
                  alt={title || "Product"}
                  className="w-12 h-12 object-cover rounded shrink-0"
                />
              )}

              <div className="flex-1 min-w-0">
                <Typography
                  variant="admin_h4"
                >
                  {title ?? contentType?.type}
                </Typography>

                {subtitles.length > 0 ? (
                  <div className="flex items-center gap-2 mt-1">{renderSubtitles()}</div>
                ) : (
                  (contentType?.label || contentType?.type) && (
                    <div className="text-admin_text">
                      {contentType?.label || contentType?.type}
                    </div>
                  )
                )}
              </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="contents md:flex md:items-center md:justify-end md:gap-2">
              {/* Action Icons */}
              <div className="order-4 md:order-none w-full md:w-auto flex gap-2 items-center flex-wrap md:justify-end empty:hidden">
                {renderHeaderActions()}
                {/* the CMS tools (copy/paste, settings, history, export,
                    install) are desktop work: a phone shows the record's own
                    actions and Save */}
                <div className="hidden md:flex gap-2 items-center">
                {showCopyPaste && isSystemAdmin && (
                  <CopyPaste
                    setLocalFieldValues={setLocalFieldValues}
                    localFieldValues={localFieldValues}
                    showSettings={dataItem?._id ? true : false}
                    onSettingsClick={() => setShowSettingsDialog(true)}
                  />
                )}
                
                {showExport && isSystemAdmin && dataItem?._id && (
                  <>
                    <Tooltip content="View changes history" position="top">
                      <Button
                        variant="admin_icon"
                        className="text-admin_text"
                        onClick={() => setShowHistoryDialog(true)}
                      >
                        <ClockIcon className="w-5 h-5" />
                      </Button>
                    </Tooltip>

                    <Tooltip content="Export this content" position="top">
                      <Button
                        variant="admin_icon"
                        className="text-admin_text"
                        onClick={handleExport}
                      >
                        <ArrowUpTrayIcon className="w-5 h-5" />
                      </Button>
                    </Tooltip>

                    <Tooltip content="Install to environment" position="top">
                      <Button
                        variant="admin_icon"
                        className="text-admin_text"
                        onClick={() => setShowInstallDialog(true)}
                      >
                        <CloudArrowUpIcon className="w-5 h-5" />
                      </Button>
                    </Tooltip>
                  </>
                )}
                </div>
              </div>

              {/* Save Button */}
              {onClickSave && (
                <div className="order-2 md:order-none ml-auto md:ml-0 flex flex-col items-center gap-1">
                  <Tooltip content={t ? t("%s changes", t(saveLabel)) : `${saveLabel} changes`} position="top">
                    <Button
                      variant="admin_primary"
                      className="w-[120px]"
                      onClick={handleSaveClick}
                    >
                      {t ? t(saveLabel) : saveLabel}
                    </Button>
                  </Tooltip>
                  {/* the record holds edits not saved yet */}
                  {(sectionsDirty || formChanges(localFieldValues, baselineRef.current, contentType).length > 0) && (
                    <span className="text-xs font-medium text-admin_warning" data-testid="save-pending">
                      pending
                    </span>
                  )}
                  {/* Status label below the button */}
                  {(localFieldValues.status || dataItem?.content?.status) && (() => {
                    const rawStatus = localFieldValues.status || dataItem?.content?.status
                    const status = typeof rawStatus === 'object' && rawStatus?.value
                      ? rawStatus.value
                      : (typeof rawStatus === 'string' ? rawStatus : null)

                    return status ? (
                      <span className="text-xs text-admin_text_secondary capitalize">
                        {status}
                      </span>
                    ) : null
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* 2nd row: Search (if enabled) */}
          {showSearch && (
            <div className="w-full flex gap-3 items-center">
              <FieldQuickSearch
                onFilteredFieldsChange={setFieldSearchData}
                contentType={contentType}
                localFieldValues={localFieldValues}
                variant={variant}
                className="w-full md:w-96"
              />
            </div>
          )}
        </div>
      )}

      {/* Scrollable Content Area — with a rail the scroll belongs to the fields
          beside it, so the rail stays put and this area does not scroll */}
      <div className={`flex-1 p-2 md:p-4 min-h-0 ${tabsOrientation === "vertical" ? "overflow-hidden" : "overflow-y-auto"}`}>
        <div className={`flex flex-col ${tabsOrientation === "vertical" ? "h-full min-h-0" : "mb-24"}`}>
        {/* Render custom type editor if defined, otherwise render groups */}
        {contentType.typeEditor ? (() => {
          const CustomTypeEditor = getTypeEditor(contentType.typeEditor)
          return CustomTypeEditor ? (
            <CustomTypeEditor
              contentType={contentType}
              fieldValues={localFieldValues}
              onChange={(updatedValues) => {
                setLocalFieldValues(updatedValues)
                if (autoSave) {
                  triggerAutoSave(updatedValues)
                }
              }}
              onSave={onSave}
              parentValues={parentValues}
              readOnly={false}
              dataItem={dataItem}
              enableFieldSettings={enableFieldSettings}
              // a type editor renders the model's extension sections itself
              language={language}
              registerSection={registerSection}
              visibilityContext={visibilityContext}
              tabsOrientation={tabsOrientation}
              helpKeyOf={helpKeyOf}
              // a tab may act on the record itself (delete it), and then the
              // dialog has to go with it
              close={onClose}
            />
          ) : renderGroups()
        })() : renderGroups()}
        </div>
      </div>

      {/* Export Dialog */}
      <CreateUpdateDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        controller={exportController}
      />

      {/* Install Dialog */}
      {showInstallDialog && (
        <EnvironmentContentInstallDialog
          content={{
            _id: dataItem?._id,
            name: localFieldValues.name || localFieldValues.title || localFieldValues.label || "Untitled",
          }}
          contentType={(() => {
            // Determine content type
            const dbResolver = DatabaseModelTypeResolver()

            // Check if it's a content type itself (type schema)
            if (contentType?.type === "CMSType" || contentType?.type === "ContentType") {
              return "ContentType"
            }

            // Priority 1: Check if dataItem has content.type (like MenuItem, BlogPost)
            if (dataItem?.content?.type) {
              return dataItem.content.type
            }

            // Priority 2: Check if localFieldValues has content.type
            if (localFieldValues.content?.type) {
              return localFieldValues.content.type
            }

            // Priority 3: Check if contentType.type is a database model
            const modelType = contentType?.type
            if (modelType && dbResolver.getType(modelType)) {
              return modelType
            }

            // Priority 4: Check localFieldValues.type
            if (localFieldValues.type && dbResolver.getType(localFieldValues.type)) {
              return localFieldValues.type
            }

            return "Content"
          })()}
          onClose={() => setShowInstallDialog(false)}
        />
      )}

      {/* History Dialog */}
      {showHistoryDialog && (
        <ContentHistoryDialog
          contentId={dataItem?._id}
          contentType={contentType?.type || 'CMSContent'}
          isOpen={showHistoryDialog}
          onClose={() => setShowHistoryDialog(false)}
        />
      )}

      {/* Settings Dialog */}
      {showSettingsDialog && dataItem && (
        <ContentSettingsDialog
          isOpen={showSettingsDialog}
          onClose={() => setShowSettingsDialog(false)}
          contentData={dataItem}
          contentType={contentType?.type || 'CMSContent'}
          showChangesOnSave={askOnSave}
          onShowChangesOnSave={(on) => {
            setShowChangesOnSave(contentType?.type, on)
            setAskOnSave(on)
          }}
          onSave={async (settings) => {
            // ContentSettingsDialog already saves metadata directly via CRUD API
            // Just refresh the data to get the updated values
            if (onRefresh) {
              await onRefresh()
            }
          }}
        />
      )}

    </div>
    )}
    </FieldClipboardProvider>
  )
}
